import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { statusQueryKey } from './mfaStatus.ts';

// Batched "which of these vendors have a second factor?" for a page of rows.
// ONE request per page, never one per row — see spec D9 and the endpoint's
// comment in apps/api/src/routes/companies.ts.

type MfaStatusRow = { company_id: string; enrolled: boolean };

export function useMfaStatuses(companyIds: string[]): Map<string, boolean> {
  const ids = useMemo(() => [...new Set(companyIds)].sort(), [companyIds]);

  const { data } = useQuery({
    queryKey: statusQueryKey(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await adminApi.get<{ statuses: MfaStatusRow[] }>(
        `/api/companies/mfa-status?ids=${ids.join(',')}`,
      );
      return new Map(res.statuses.map((row) => [row.company_id, row.enrolled]));
    },
  });

  // An empty map on error or while loading means MfaBadge renders nothing —
  // the badges stay hidden rather than claiming every vendor is unprotected.
  return data ?? new Map<string, boolean>();
}
